import React from "react";
import styled from "@emotion/styled";

/**
 * 스위처 컴포넌트
 * @param checked 체크 상태
 * @param onChange 체크 상태 변경 핸들러
 * @param className 컴포넌트 클래스명
 * @param id 컴포넌트 아이디
 * @param size 스위처 크기
 * @param switcherColor 스위치 색상 (default : #17171C, primary : #047BBC, success : #06C167)
 * @param showLabel 라벨 표시 여부
 * @param onLabel 체크 상태 라벨
 * @param offLabel 미체크 상태 라벨
 * @param disabled 비활성화 상태
 */

/**
 * 스위처 컴포넌트의 props 타입 정의
 */
interface SwitcherProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "onChange"> {
  /** 체크 상태 */
  checked: boolean;
  /** 체크 상태 변경 핸들러 */
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** 컴포넌트 클래스명 */
  className?: string;
  /** 컴포넌트 아이디 */
  id?: string;
  /** 스위처 크기 */
  size?: "sm" | "md" | "lg";
  /** 스위치 색상 */
  switcherColor?: "default" | "primary" | "success";
  /** 라벨 표시 여부 */
  showLabel?: boolean;
  /** 체크 상태 라벨 */
  onLabel?: string;
  /** 미체크 상태 라벨 */
  offLabel?: string;
  /** 비활성화 상태 */
  disabled?: boolean;
}

/**
 * Track 컴포넌트의 props 타입 정의
 */
interface TrackProps {
  size?: SwitcherProps["size"];
  color?: SwitcherProps["switcherColor"];
}

/**
 * Dot 컴포넌트의 props 타입 정의
 */
interface DotProps {
  size?: SwitcherProps["size"];
}

const SwitcherLabel = styled.label`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  position: relative;
`;

const HiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`;

const Track = styled("div", {
  shouldForwardProp: (prop: string) => !["size", "color"].includes(prop),
})<TrackProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  padding-left: 8px;
  padding-right: 8px;
  position: relative;
  border-radius: 9999px;
  transition: background-color 0.2s ease-in-out;
  border: 1px solid #9ea2a4;
  background-color: #bbbdbe;

  ${({ size }) => {
    switch (size) {
      case "sm":
        return `
          width: 44px;
          height: 24px;
        `;
      case "md":
        return `
          width: 64px;
          height: 32px;
        `;
      case "lg":
        return `
          width: 104px;
          height: 42px;
        `;
      default:
        return `
          width: 54px;
          height: 30px;
        `;
    }
  }}

  ${HiddenInput}:checked + & {
    background-color: ${({ color }) => {
      switch (color) {
        case "primary":
          return "#047BBC";
        case "success":
          return "#06C167";
        default:
          return "#17171C";
      }
    }};
  }
`;

const Dot = styled("div", {
  shouldForwardProp: (prop: string) => !["size"].includes(prop),
})<DotProps>`
  position: absolute;
  background-color: white;
  border: 1px solid #9fa3a5;
  border-radius: 9999px;
  transition: transform 0.2s ease-in-out;

  ${({ size }) => {
    switch (size) {
      case "sm":
        return `
          width: 16px;
          height: 16px;
          top: 3px;
          left: 3px;
        `;
      case "md":
        return `
          width: 24px;
          height: 24px;
          top: 3px;
          left: 3px;
        `;
      case "lg":
        return `
          width: 27px;
          height: 27px;
          top: 6px;
          left: 7px;
        `;
      default:
        return `
          width: 20px;
          height: 20px;
          top: 4px;
          left: 5px;
        `;
    }
  }}

  ${HiddenInput}:checked ~ & {
    transform: translateX(
      ${({ size }) => {
        switch (size) {
          case "sm":
            return "20px";
          case "md":
            return "30px";
          case "lg":
            return "60px";
          default:
            return "24px";
        }
      }}
    );
  }
`;

const LabelBase = styled.p`
  font-size: 16px;
  line-height: 40px;
  font-weight: 400;
  transition: opacity 0.2s ease-in-out;
  color: white;
  position: absolute;
  margin: 0;
`;

const LabelOn = styled(LabelBase)`
  opacity: 0;
  left: 24px;

  ${HiddenInput}:checked + ${Track} > & {
    opacity: 1;
  }
`;

const LabelOff = styled(LabelBase)`
  opacity: 1;
  right: 18px;

  ${HiddenInput}:checked + ${Track} > & {
    opacity: 0;
  }
`;

export const Switcher: React.FC<SwitcherProps> = ({
  checked,
  onChange,
  className,
  id,
  size = "md",
  switcherColor = "default",
  showLabel = false,
  onLabel,
  offLabel,
  disabled,
  ...rest
}) => {
  return (
    <SwitcherLabel htmlFor={id} className={className} role="switch" aria-checked={checked} aria-disabled={disabled}>
      <HiddenInput type="checkbox" id={id} checked={checked} onChange={onChange} disabled={disabled} {...rest} />
      <Track size={size} color={switcherColor}>
        {showLabel && (
          <>
            <LabelOn>{onLabel}</LabelOn>
            <LabelOff>{offLabel}</LabelOff>
          </>
        )}
      </Track>
      <Dot size={size} />
    </SwitcherLabel>
  );
};

Switcher.displayName = "Switcher";
